import React, { useMemo, useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence, useScroll, useTransform, useMotionValue } from 'framer-motion'
import { ArrowLeft, ExternalLink, X, ChevronLeft, ChevronRight, Zap, Sparkles, Share2 } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { projectApi, assetUrl } from '../services/api'
import { getIconComponent } from '../utils/iconUtils'

type Project = {
  id: number;
  title: string;
  category?: string;
  description: string;
  technologies: (string | { name: string; icon_type?: 'library' | 'upload'; icon_value?: string })[];
  image?: string | null;
  button_text?: string;
  live_demo_url?: string | null;
  github_url?: string | null;
  gallery?: string[];
  testimonials?: { author?: string; text: string }[];
  case_study_content?: string;
  slug: string;
  views_count?: number;
  client?: string;
  date?: string;
  tech_heading?: string;
  tech_heading_icon_type?: 'library' | 'upload';
  tech_heading_icon_value?: string;
  subtitle?: string; // Added to match Showcase type if needed, or map from description/category
};

type FetchedProject = {
  id: number | string;
  title?: string;
  category?: string;
  description?: string;
  technologies?: (string | { name: string; icon_type?: 'library' | 'upload'; icon_value?: string })[];
  tags?: string[];
  image?: string | null;
  image_url?: string | null;
  button_text?: string;
  live_demo_url?: string | null;
  github_url?: string | null;
  gallery?: string[];
  testimonials?: { author?: string; text: string }[];
  case_study_content?: string;
  slug?: string;
  views_count?: number;
  client?: string;
  date?: string;
  link?: string;
  tech_heading?: string;
  tech_heading_icon_type?: 'library' | 'upload';
  tech_heading_icon_value?: string;
};

const SingleProjectPage: React.FC = () => {
  const navigate = useNavigate()
  const { slug } = useParams()
  const [project, setProject] = useState<Project | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const { scrollYProgress } = useScroll()
  const heroY = useTransform(scrollYProgress, [0, 1], ['0%', '12%'])
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.06])
  const bgY1 = useTransform(scrollYProgress, [0, 1], [0, 300])
  const bgY2 = useTransform(scrollYProgress, [0, 1], [0, -300])

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)
  
  const gallery = useMemo(() => (project && Array.isArray(project.gallery)) ? project.gallery : [], [project])
  
  const tiltX = useMotionValue(0)
  const tiltY = useMotionValue(0)
  const rotateX = useTransform(tiltY, [-0.5, 0.5], [8, -8])
  const rotateY = useTransform(tiltX, [-0.5, 0.5], [-8, 8])
  const glowX = useTransform(tiltX, [-0.5, 0.5], ['20%', '80%'])
  const glowY = useTransform(tiltY, [-0.5, 0.5], ['20%', '80%'])

  const touchStartX = useRef<number | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        
        let fetched: FetchedProject | null = null;

        // Fetch Logic (similar to ProjectDetail)
        if (slug) {
            const isNumeric = /^\d+$/.test(slug);
            const allRes = await projectApi.getAll();
            let relatedPool: FetchedProject[] = [];
            const rawList: unknown = Array.isArray(allRes) ? allRes : (allRes?.success ? allRes.data : []);
            if (Array.isArray(rawList) && rawList.length) {
                const list = (rawList as FetchedProject[]).map((p) => ({
                    ...p,
                    slug: p.slug || String(p.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
                }));
                relatedPool = list;
                const match = list.find((p) => p.slug === slug);
                if (match) fetched = match;
            }

            if (!fetched) {
                if (isNumeric) {
                    const byId = await projectApi.getById(Number(slug));
                    const byIdData = (byId?.success ? byId.data : byId) as FetchedProject | null;
                    if (byIdData) fetched = byIdData;
                } else {
                    const bySlug = await projectApi.getBySlug(slug);
                    const bySlugData = (bySlug?.success ? bySlug.data : bySlug) as FetchedProject | null;
                    if (bySlugData) fetched = bySlugData;
                }
            }
        } else {
            // Default to first project if no slug provided (for /project/single-page route)
            const all = await projectApi.getAll();
            if (all?.success && Array.isArray(all.data) && all.data.length) {
                fetched = all.data[0] as FetchedProject;
            }
        }

        const normalize = (f: FetchedProject): Project => ({
          id: typeof f.id === 'string' ? Number(f.id) : f.id,
          title: f.title || '',
          category: f.category,
          description: f.description || '',
          technologies: Array.isArray(f.technologies) && f.technologies.length > 0
            ? f.technologies
            : Array.isArray(f.tags)
            ? f.tags.map(t => typeof t === 'string' ? t : { name: String(t), icon_type: 'library', icon_value: 'Box' })
            : [],
          image: f.image || f.image_url || null,
          button_text: f.button_text,
          live_demo_url: f.live_demo_url || f.link || null,
          github_url: f.github_url || null,
          gallery: Array.isArray(f.gallery) ? f.gallery : [],
          testimonials: Array.isArray(f.testimonials) ? f.testimonials : [],
          case_study_content: f.case_study_content,
          slug: f.slug || String(f.title || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
          views_count: f.views_count,
          client: f.client,
          date: f.date,
          tech_heading: f.tech_heading,
          tech_heading_icon_type: f.tech_heading_icon_type,
          tech_heading_icon_value: f.tech_heading_icon_value,
          subtitle: f.description ? f.description.substring(0, 100) + '...' : '' // Mock subtitle
        });

        if (fetched) {
          const normalized: Project = normalize(fetched);
          setProject(normalized);
          if (normalized.id) {
            try { await projectApi.incrementViews(normalized.id); } catch { void 0; }
          }
        } else {
          setError('Project not found');
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Failed to load project';
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#030014] text-white">Loading...</div>;
  if (error || !project) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#030014] text-white">
        <p className="text-gray-400">{error || 'Project not found'}</p>
        <button className="px-4 py-2 bg-white/10 rounded-full" onClick={() => navigate('/projects')}>Back to Projects</button>
    </div>
  );

  return (
    <section className="min-h-screen bg-[#030014] text-white">
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <motion.div style={{ y: bgY1 }} className="absolute top-[-10%] left-[-10%] w-[55vw] h-[55vw] bg-purple-900/10 blur-[120px] rounded-full opacity-50" />
        <motion.div style={{ y: bgY2 }} className="absolute bottom-[-10%] right-[-10%] w-[55vw] h-[55vw] bg-cyan-900/10 blur-[120px] rounded-full opacity-50" />
      </div>
      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="relative mb-8 overflow-hidden rounded-xl border border-white/10">
          <motion.div animate={{ x: ['0%', '-50%'] }} transition={{ duration: 20, repeat: Infinity, ease: 'linear' }} className="flex gap-10 whitespace-nowrap py-3 px-6 text-xs uppercase tracking-[0.2em] text-white/70">
            <span className="inline-flex items-center gap-2"><Sparkles size={14} /> Interactive</span>
            <span className="inline-flex items-center gap-2"><Zap size={14} /> Generative</span>
            <span className="inline-flex items-center gap-2"><Sparkles size={14} /> Reactive</span>
            <span className="inline-flex items-center gap-2"><Zap size={14} /> Immersive</span>
            <span className="inline-flex items-center gap-2"><Sparkles size={14} /> Interactive</span>
            <span className="inline-flex items-center gap-2"><Zap size={14} /> Generative</span>
            <span className="inline-flex items-center gap-2"><Sparkles size={14} /> Reactive</span>
            <span className="inline-flex items-center gap-2"><Zap size={14} /> Immersive</span>
          </motion.div>
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)', backgroundSize: '4px 4px' }} />
        </div>
        <div className="flex items-center justify-between mb-6">
          <button className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 border border-white/20" onClick={() => navigate('/projects')}>
            <ArrowLeft size={16} /> Back to Portfolio
          </button>
          {project.live_demo_url && (
             <a href={project.live_demo_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 hover:bg-cyan-500/30 transition-colors">
                {project.button_text || 'View Case Study'} <ExternalLink size={16} />
             </a>
          )}
        </div>

        <div className="space-y-2 mb-4">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-pink-400 font-mono uppercase tracking-widest">{project.category}</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-4xl md:text-6xl font-display font-bold">
            {project.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="text-white/70 text-lg">{project.subtitle || project.description.substring(0, 100)}</motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
          <div className="md:col-span-2 space-y-6">
            <div className="relative w-full h-[50vh] md:h-[60vh] overflow-hidden rounded-2xl border border-white/10" onMouseMove={(e) => {
              const r = (e.currentTarget as HTMLDivElement).getBoundingClientRect()
              const nx = (e.clientX - r.left) / r.width - 0.5
              const ny = (e.clientY - r.top) / r.height - 0.5
              tiltX.set(nx)
              tiltY.set(ny)
            }} onMouseLeave={() => { tiltX.set(0); tiltY.set(0) }}>
              <motion.div style={{ y: heroY, scale: heroScale, rotateX, rotateY, transformPerspective: 1000 }} className="absolute inset-0">
                <img src={assetUrl(project.image || '')} alt={project.title} className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?q=80&w=2070&auto=format&fit=crop' }} />
              </motion.div>
              <motion.div 
                style={{ left: glowX, top: glowY, background: 'radial-gradient(closest-side, rgba(255,255,255,0.20), rgba(255,255,255,0.0))' }} 
                className="absolute w-[35vmin] h-[35vmin] -translate-x-1/2 -translate-y-1/2 rounded-full" 
                animate={{ opacity: [0.15, 0.35, 0.15] }} transition={{ duration: 3, repeat: Infinity }}
              />
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 60, repeat: Infinity, ease: 'linear' }} className="absolute inset-0 opacity-[0.07]" 
                style={{ backgroundImage: 'linear-gradient(transparent 98%, rgba(255,255,255,0.7) 2%)', backgroundSize: '8px 8px' }} />
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="absolute inset-0 bg-gradient-to-t from-[#030014] via-[#030014]/40 to-transparent" />
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 0.2 }} transition={{ duration: 1 }} className="absolute inset-0" style={{ backgroundImage: `url(${assetUrl(project.image || '')}) , radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)`, backgroundSize: 'cover, 3px 3px', backgroundPosition: 'center, center', backgroundBlendMode: 'soft-light' }} />
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                <div className="px-4 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-sm text-white/90">Featured</div>
                <div className="flex gap-2">
                  <button onClick={() => setLightboxIndex(selectedIndex)} className="px-4 py-2 rounded-full bg-white text-black text-sm">Fullscreen</button>
                  {project.live_demo_url && (
                    <a 
                      href={project.live_demo_url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="px-4 py-2 rounded-full bg-cyan-500 hover:bg-cyan-600 text-white text-sm flex items-center gap-2 transition-colors"
                    >
                      {project.button_text || 'View Case Study'} <ExternalLink size={14} />
                    </a>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="px-4 py-3 rounded-xl bg-white/5 border border-white/10">
                <div className="text-xs text-white/60">Views</div>
                <div className="text-lg font-semibold">{project.views_count || 0}</div>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="px-4 py-3 rounded-xl bg-white/5 border border-white/10">
                <div className="text-xs text-white/60">Client</div>
                <div className="text-lg font-semibold truncate">{project.client || 'N/A'}</div>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="px-4 py-3 rounded-xl bg-white/5 border border-white/10">
                <div className="text-xs text-white/60">Date</div>
                <div className="text-lg font-semibold">{project.date || 'N/A'}</div>
              </motion.div>
            </div>

            <motion.p initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-lg text-gray-300 leading-relaxed">{project.description}</motion.p>
            
            {project.case_study_content && (
              <div className="prose prose-invert max-w-none">
                <div dangerouslySetInnerHTML={{ __html: project.case_study_content }} />
              </div>
            )}

            {gallery.length > 0 && (
            <div className="space-y-4">
              <motion.h3 initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="text-2xl font-bold">Gallery</motion.h3>
              <div className="relative group">
                <div className="relative w-full h-[50vh] md:h-[60vh] overflow-hidden rounded-2xl border border-white/10">
                  <AnimatePresence mode="sync">
                    <motion.div key={selectedIndex} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} className="absolute inset-0">
                      <img src={assetUrl(gallery[selectedIndex])} alt="Gallery" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?q=80&w=2070&auto=format&fit=crop' }} />
                    </motion.div>
                  </AnimatePresence>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent" />
                  <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)', backgroundSize: '3px 3px', mixBlendMode: 'soft-light' }} />
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between">
                    <div className="px-4 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-sm text-white/90">Image {selectedIndex + 1} / {gallery.length}</div>
                    <div className="flex gap-2">
                      <button onClick={() => setSelectedIndex((i) => (i - 1 + gallery.length) % gallery.length)} className="px-3 py-2 rounded-lg bg-white/10 border border-white/20">Prev</button>
                      <button onClick={() => setSelectedIndex((i) => (i + 1) % gallery.length)} className="px-3 py-2 rounded-lg bg-white/10 border border-white/20">Next</button>
                      <button onClick={() => setLightboxIndex(selectedIndex)} className="px-3 py-2 rounded-lg bg-white text-black">Expand</button>
                    </div>
                  </div>
                </div>
                <div className="mt-3 overflow-x-auto no-scrollbar">
                  <div className="flex gap-3">
                    {gallery.map((src, i) => (
                      <button key={i} onClick={() => setSelectedIndex(i)} className={`relative w-24 h-16 rounded-lg overflow-hidden border ${selectedIndex === i ? 'border-pink-400' : 'border-white/10'} hover:border-pink-400/60 transition-colors`}>
                        <img src={assetUrl(src)} alt="Thumb" className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" loading="lazy" onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?q=80&w=300&auto=format&fit=crop' }} />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="md:columns-3 columns-2 gap-4">
                  {gallery.map((src, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.04 }} className="mb-4 break-inside-avoid">
                      <button onClick={() => setLightboxIndex(i)} className="group relative block w-full overflow-hidden rounded-xl border border-white/10">
                        <img src={assetUrl(src)} alt="Gallery" loading="lazy" className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.03]" onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?q=80&w=1200&auto=format&fit=crop' }} />
                        <div className="absolute inset-0 bg-gradient-to-tr from-pink-500/10 via-transparent to-cyan-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        <div className="absolute bottom-3 left-3 right-3 px-3 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-xs text-white/90">Tap to view</div>
                        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '3px 3px', mixBlendMode: 'overlay' }} />
                      </button>
                    </motion.div>
                  ))}
                </div>
              </div>

              <AnimatePresence>
                {lightboxIndex !== null && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} className="fixed inset-0 z-[200] bg-black/80 backdrop-blur-sm" onTouchStart={(e) => { touchStartX.current = e.changedTouches[0].clientX }} onTouchEnd={(e) => { const endX = e.changedTouches[0].clientX; const startX = touchStartX.current ?? endX; const delta = endX - startX; if (Math.abs(delta) > 40) { setLightboxIndex((i) => i === null ? i : (delta < 0 ? ((i + 1) % gallery.length) : ((i - 1 + gallery.length) % gallery.length))); } touchStartX.current = null; }} onClick={() => setLightboxIndex(null)}>
                    <div className="absolute inset-0 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                      <motion.div key={lightboxIndex} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }} transition={{ duration: 0.35 }} className="relative w-[90vw] h-[80vh] max-w-6xl overflow-hidden rounded-2xl border border-white/20">
                        <img src={assetUrl(gallery[lightboxIndex])} alt="Lightbox" className="w-full h-full object-contain bg-black" onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1496181133206-80ce9b88a853?q=80&w=1600&auto=format&fit=crop' }} />
                        <button onClick={() => setLightboxIndex(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white text-black flex items-center justify-center">
                          <X size={18} />
                        </button>
                        <button onClick={() => setLightboxIndex((i) => i === null ? i : ((i - 1 + gallery.length) % gallery.length))} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 border border-white/30 backdrop-blur-md flex items-center justify-center">
                          <ChevronLeft size={18} />
                        </button>
                        <button onClick={() => setLightboxIndex((i) => i === null ? i : ((i + 1) % gallery.length))} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 border border-white/30 backdrop-blur-md flex items-center justify-center">
                          <ChevronRight size={18} />
                        </button>
                      </motion.div>
                    </div>
                    <div className="absolute inset-0" onClick={() => setLightboxIndex(null)} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            )}
          </div>

          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-2 mb-3">
                 {(() => {
                    const iconType = project.tech_heading_icon_type || 'library';
                    const iconValue = project.tech_heading_icon_value || 'Cpu';
                    if (iconType === 'library') {
                        const Icon = getIconComponent(iconValue);
                        return <Icon size={18} className="text-cyan-400" />;
                    }
                    if (iconType === 'upload' && iconValue) {
                        return <img src={assetUrl(iconValue)} alt="" className="w-5 h-5 object-contain" />;
                    }
                    return null;
                 })()}
                 <h4 className="text-sm font-bold uppercase tracking-wider">{project.tech_heading || 'Technologies'}</h4>
              </div>
              <div className="flex flex-wrap gap-2">
                {project.technologies.map((t, i) => {
                  const name = typeof t === 'string' ? t : t.name;
                  const iconType = typeof t === 'string' ? 'library' : t.icon_type;
                  const iconValue = typeof t === 'string' ? 'Box' : t.icon_value;
                  
                  let IconElement = null;
                  if (iconType === 'library') {
                    const LibIcon = getIconComponent(iconValue || 'Box');
                    IconElement = <LibIcon size={14} className="mr-1" />;
                  } else if (iconType === 'upload' && iconValue) {
                    IconElement = <img src={assetUrl(iconValue)} alt="" className="w-4 h-4 mr-1 object-contain" />;
                  }
                  
                  return (
                    <motion.span key={i} initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }} className="flex items-center px-3 py-1 text-sm bg-white/5 border border-white/10 rounded-full text-gray-300">
                       {IconElement}
                       {name}
                    </motion.span>
                  );
                })}
              </div>
            </div>
            {Array.isArray(project.testimonials) && project.testimonials.length > 0 && (
              <div>
                <h4 className="text-sm font-bold uppercase tracking-wider mb-3">Testimonials</h4>
                <div className="space-y-4">
                  {(project.testimonials as { author?: string; text: string }[]).map((t: { author?: string; text: string }, i: number) => (
                    <blockquote key={i} className="p-4 bg-white/5 border border-white/10 rounded-lg">
                      <p className="text-gray-300 italic">"{t.text}"</p>
                      {t.author && <cite className="block mt-2 text-sm text-gray-500 not-italic">— {t.author}</cite>}
                    </blockquote>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default SingleProjectPage
